import React from "react";

import { Button, Drawer, Form, Input, FormInstance } from "antd";
import {
  ReloadOutlined,
  PlusCircleOutlined,
  MinusCircleOutlined,
} from "@ant-design/icons";

import { usePermissions } from "hooks/usePermissions";
import PermissionWrapper from "shared/components/PermissionWrapper";
import { CREATE_HELIX_TEMPLATES_PERMISSION, HelixTemplate } from "./Helix";
import ActionButton from "stories/components/ActionButton/ActionButton";

type Handlers = {
  showDrawer: () => void;
  closeDrawer: () => void;
  onFinishNewTemplate: (values: any) => void;
  handleRefresh: () => void;
};

type Values = {
  form: FormInstance;
  visibleDrawer: boolean;
  helixTemplates: HelixTemplate[];
};

type Props = {
  handlers: Handlers;
  values: Values;
};

const AddHelixTemplate: React.FC<Props> = ({ handlers, values }) => {
  const { showDrawer, closeDrawer, onFinishNewTemplate, handleRefresh } = handlers;
  const { form, visibleDrawer, helixTemplates } = values;

  const { hasPermissions } = usePermissions();
  const canCreateHelixTemplate = hasPermissions([CREATE_HELIX_TEMPLATES_PERMISSION]);

  return (
    <>
      <PermissionWrapper
        hasPermissions={canCreateHelixTemplate}
        tooltip={!canCreateHelixTemplate ? "No permission to add a new Helix template." : ""}
      >
        <ActionButton
          onClick={showDrawer}
          text="Add Template"
          type="primary"
          disabled={!canCreateHelixTemplate}
          icon={
            !visibleDrawer ? (
              <PlusCircleOutlined style={{ fontSize: "15px" }} />
            ) : (
              <MinusCircleOutlined style={{ fontSize: "15px" }} />
            )
          }
        />
      </PermissionWrapper>
      <ActionButton onClick={handleRefresh} text="Refresh" icon={<ReloadOutlined />} type="default" />
      <Drawer
        title="Add Helix Template"
        placement="right"
        onClose={closeDrawer}
        open={visibleDrawer}
        width={500}
        closable={true}
        className="manage-pages-drawer"
        forceRender
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinishNewTemplate}
          name="newTemplateForm"
        >
          <Form.Item
            name="nameValue"
            label="Name"
            rules={[{ required: true, message: "Please enter Helix Template name" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="helixIdValue"
            label="Helix Template ID"
            rules={[
              { required: true, message: "Please enter Helix Template ID" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const idValue = getFieldValue("idValue");
                  const helixIdValues = helixTemplates.map((item) => ({
                    id: item._id,
                    helixId: item.helixId,
                  }));

                  const matchedHelixId = helixIdValues.filter(
                    (item) => item.helixId === value && item.id !== idValue
                  );

                  if (matchedHelixId.length === 0) {
                    return Promise.resolve();
                  } else {
                    return Promise.reject(
                      new Error(`ID: ${value} already exists, please enter a new id`)
                    );
                  }
                },
              }),
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="descValue"
            label="Description"
            rules={[{ required: true, message: "Please enter Helix a description" }]}
          >
            <Input.TextArea />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit">
              Save Template
            </Button>
          </Form.Item>
        </Form>
      </Drawer>
    </>
  );
};

export default AddHelixTemplate;
